import {ConfiguredImageDto, ConfiguredProductDto, DetailedImageRequirementDto, ImageDto, ImageFilterDto, ImageRequirementDto, PersonDto, ProductMediumDto} from '@swagger';

/** Choices made by the customer, independent of inferred eligibility and DTO defaults. */
export interface ConfigurationChoices {
    personCode?: string;
    mode?: 'AUTO' | 'DETAILED';
}

/** Explicit, dialog-local context; ordinary product configuration has no prefill. */
export interface ProductConfigurationPrefill {
    configuration: ConfiguredProductDto;
    personCodes: string[];
    choices?: ConfigurationChoices;
}

export function sourcePersonCodes(configurations: ConfiguredProductDto[], persons: PersonDto[]): string[] {
    if (!configurations.length || configurations.some(configuration => !configuration.images?.length)) return [];
    return persons.filter(person => configurations.every(configuration => person.productIds.includes(configuration.productId)
        && configuration.images.every(image => person.imageIds.includes(image.uniqueImageId)))).map(person => person.code);
}

/** Keep only compatible choices. Empty slots must still be completed in the normal selector. */
export function mapConfigurationPrefill(prefill: ProductConfigurationPrefill, requirements: DetailedImageRequirementDto[],
                                        images: ImageDto[], detailed: boolean): ConfiguredImageDto[][] {
    const source = prefill.configuration.images ?? [];
    const usedDownloads = new Set<string>();
    const usedSourceOccurrences = new Set<number>();
    return requirements.map(target => {
        const eligible = images.filter(image => target.requirement.types.includes(image.type)
            && target.requirement.orientations.includes(image.orientation));
        let candidates = source.filter(choice => eligible.some(image => image.uniqueId === choice.uniqueImageId));
        const all = target.requirement.type === ImageRequirementDto.TypeEnum.ALLOFPERSON;
        // A simple set's assigned ALL downloads are not the customer's shared print choice.
        if (!all && source.some(choice => choice.productId == null)) {
            candidates = candidates.filter(choice => choice.productId == null);
        }
        if (detailed || all) {
            // Stable sorting retains the original choice order within each preference.
            candidates = [...candidates.filter(choice => choice.productId === target.productId),
                ...candidates.filter(choice => choice.productId !== target.productId)];
        }
        if (!all && target.productMedium === ProductMediumDto.DOWNLOAD) {
            candidates = candidates.filter(choice => !usedDownloads.has(choice.uniqueImageId));
        }
        // Repeated print slots may deliberately contain different photos. Match
        // retained leaf slots by occurrence before reusing a general choice for an
        // added slot. Track array positions, since identical photos can occur twice.
        const exactIndex = detailed && !all ? source.findIndex((choice, index) =>
            choice.productId === target.productId && !usedSourceOccurrences.has(index) && candidates.includes(choice)) : -1;
        const selected = all ? eligible.map(image => candidates.find(choice => choice.uniqueImageId === image.uniqueId)
                ?? {uniqueImageId: image.uniqueId, imageId: image.id, imageCropId: image.imageCropId})
            : exactIndex >= 0 ? [source[exactIndex]] : candidates.slice(0, 1);
        if (exactIndex >= 0) usedSourceOccurrences.add(exactIndex);
        if (!all && target.productMedium === ProductMediumDto.DOWNLOAD) {
            selected.forEach(choice => usedDownloads.add(choice.uniqueImageId));
        }
        return selected.map(choice => ({
            ...choice, productId: detailed || all ? target.productId : undefined,
            filter: target.productFilter ?? choice.filter ?? ImageFilterDto.NONE
        }));
    });
}
